import axios from 'axios';
import { API_BASE_URL } from '../../config/apiBase';
import { getWellandStoredToken } from '../../auth/storageKeys';
import { refreshAccessToken } from '../../auth/refreshAccessToken';

const API_BASE = API_BASE_URL;

const apiClient = axios.create();

apiClient.interceptors.request.use((config) => {
    const token = getWellandStoredToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// При 401 — один раз обновляем токен и повторяем запрос.
// Параллельные 401 ставятся в очередь и получают тот же новый токен.
let _refreshing = false;
let _queue = [];

const flushQueue = (token, err) => {
    _queue.forEach(({ resolve, reject }) => (token ? resolve(token) : reject(err)));
    _queue = [];
};

apiClient.interceptors.response.use(
    (res) => res,
    async (error) => {
        const original = error.config;
        if (error.response?.status !== 401 || original._retry) {
            return Promise.reject(error);
        }
        if (_refreshing) {
            return new Promise((resolve, reject) => {
                _queue.push({ resolve, reject });
            }).then((token) => {
                original.headers = original.headers || {};
                original.headers.Authorization = `Bearer ${token}`;
                return apiClient(original);
            });
        }
        original._retry = true;
        _refreshing = true;
        try {
            const newToken = await refreshAccessToken();
            if (newToken) {
                original.headers = original.headers || {};
                original.headers.Authorization = `Bearer ${newToken}`;
                flushQueue(newToken, null);
                return apiClient(original);
            }
        } catch (e) {
            flushQueue(null, e);
        } finally {
            _refreshing = false;
        }
        return Promise.reject(error);
    }
);

/**
 * Единый сервис для всех расчетов оборудования на бэкенде.
 */
export class CalculationsAPI {
    static normalizeResponse(raw) {
        if (raw && typeof raw === 'object' && 'ok' in raw) {
            if (raw.ok) return { ok: true, data: raw.data };
            return { ok: false, data: null, error: raw.error || { message: 'Unknown error' } };
        }

        return { ok: true, data: raw };
    }

    static sanitizeConsoleState(state) {
        const s = state && typeof state === 'object' ? state : {};

        const pick = (key) => {
            const v = s[key];
            if (!v) return null;
            if (typeof v !== 'object') return v;

            const {
                id,
                name,
                type,
                quantity,
                price,
                calculatedPrice,
                flowM3h,
                capacity
            } = v;

            const out = { id, name, type, quantity, price, calculatedPrice, flowM3h, capacity };
            Object.keys(out).forEach(k => out[k] === undefined && delete out[k]);
            return out;
        };

        return {
            selectedPump: pick('selectedPump'),
            selectedFilter: pick('selectedFilter'),
            selectedHeatExchanger: pick('selectedHeatExchanger'),
            selectedElectricHeater: pick('selectedElectricHeater'),
            selectedHeatPump: pick('selectedHeatPump'),
            selectedChlorinator: pick('selectedChlorinator'),
            selectedUltraviolet: pick('selectedUltraviolet'),
            selectedProjector: pick('selectedProjector'),
            selectedHeatingType: s.selectedHeatingType || null,
            lightingNotNeeded: s.lightingNotNeeded || null
        };
    }

    // --- Универсальные методы ---

    /**
     * Получить рекомендации для любого типа оборудования.
     * @param {object} [requestOptions] — например `{ signal }` из AbortController (отмена при смене шага)
     */
    static async getRecommendations(equipmentType, payload, requestOptions = {}) {
        try {
            const response = await apiClient.post(
                `${API_BASE}/api/calculations/${equipmentType}/recommend`,
                payload,
                requestOptions
            );
            return this.normalizeResponse(response.data).data;
        } catch (error) {
            if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
                return undefined;
            }
            console.error(`Ошибка при получении рекомендаций для ${equipmentType}:`, error);
            return null;
        }
    }

    /**
     * Batch-обёртка: несколько recommend-задач за один RTT.
     * @param {Array<{type: string, payload: object}>} tasks
     * @param {object} [requestOptions]
     */
    static async getBatchRecommendations(tasks, requestOptions = {}) {
        try {
            const response = await apiClient.post(
                `${API_BASE}/api/calculations/batch`,
                { tasks: Array.isArray(tasks) ? tasks : [] },
                requestOptions
            );
            const data = this.normalizeResponse(response.data).data;
            return data?.results || {};
        } catch (error) {
            if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
                return undefined;
            }
            console.error('Ошибка batch рекомендаций:', error);
            return null;
        }
    }

    /**
     * Получить технические расчеты для заданного оборудования.
     */
    static async getTechnicalInfo(equipmentType, payload, requestOptions = {}) {
        try {
            const response = await apiClient.post(
                `${API_BASE}/api/calculations/${equipmentType}/technical-info`,
                payload,
                requestOptions
            );
            return this.normalizeResponse(response.data).data;
        } catch (error) {
            if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
                return undefined;
            }
            console.error(`Ошибка при получении тех. инфо для ${equipmentType}:`, error);
            return null;
        }
    }

    // --- Специфичные обертки (Удобство) ---

    // 1. Насосы
    static async getPumpRecommendations(allEquipment, poolData, requestOptions = {}) {
        const data = await this.getRecommendations('pump', {
            allEquipment: Array.isArray(allEquipment) ? allEquipment : [],
            poolData: poolData || {}
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [], specs: null };
    }

    /**
     * Публичный конфиг гидравлики насоса (server/src/core/calculations/pump/config.js).
     * @returns {Promise<{ config: Object, fingerprint: string }|null>}
     */
    static async getPumpHydraulicsConfig() {
        try {
            const response = await apiClient.get(`${API_BASE}/api/calculations/pump/hydraulics-config`);
            const norm = this.normalizeResponse(response.data);
            return norm.ok ? norm.data : null;
        } catch (error) {
            console.error('Ошибка getPumpHydraulicsConfig:', error);
            return null;
        }
    }

    /**
     * Требуемый поток и цикл фильтрации по тем же формулам, что подбор насоса.
     * @param {{ length?: number, width?: number, depth?: number, volume?: number }} poolDimensions
     * @param {import('axios').GenericAbortSignal} [requestOptions.signal]
     * @returns {Promise<Object|null>}
     */
    static async getPumpHydraulicsPreview(poolDimensions, requestOptions = {}) {
        try {
            const response = await apiClient.post(
                `${API_BASE}/api/calculations/pump/hydraulics-preview`,
                { poolData: poolDimensions || {} },
                requestOptions,
            );
            const norm = this.normalizeResponse(response.data);
            return norm.ok ? norm.data : null;
        } catch (error) {
            if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') {
                return undefined;
            }
            console.error('Ошибка getPumpHydraulicsPreview:', error);
            return null;
        }
    }

    static async getPumpTechnicalInfo(pump, poolVolume, poolType) {
        // poolType — для честного цикла фильтрации переливного (объём системы
        // = чаша + компенсационная ёмкость на сервере)
        const data = await this.getTechnicalInfo('pump', { pump, poolVolume, poolType });
        return data || null;
    }

    /**
     * Метрики бассейна с бэкенда (core/pool): объём, площади, техпомещение.
     * @param {Object} pool 
     */
    static async getPoolMetrics(pool) {
        try {
            const response = await apiClient.post(`${API_BASE}/api/calculations/pool/metrics`, { pool: pool || {} });
            const norm = this.normalizeResponse(response.data);
            return norm.ok ? norm.data : null;
        } catch (error) {
            console.error('Ошибка getPoolMetrics:', error);
            return null;
        }
    }

    // 2. Хлоринаторы
    static async calculateRecommendedChlorinators(selectedPump, poolDimensions, allEquipment, requestOptions = {}) {
        const data = await this.getRecommendations('chlorinators', {
            selectedPump,
            poolDimensions,
            allEquipment: Array.isArray(allEquipment) ? allEquipment : []
        }, requestOptions);
        if (data === undefined) return undefined;
        return data?.recommendations || [];
    }

    static async getChlorinatorTechnicalInfo(chlorinator, poolVolume, filtrationCycleHours) {
        const data = await this.getTechnicalInfo('chlorinators', { chlorinator, poolVolume, filtrationCycleHours });
        // New backend returns info as data directly
        return data || { chlorinePerCycle: null };
    }

    // 3. Фильтры
    static async getFilterRecommendations(allEquipment, selectedPump, requestOptions = {}) {
        const data = await this.getRecommendations('filters', { allEquipment, selectedPump }, requestOptions);
        if (data === undefined) return undefined;
        return data?.recommendations || [];
    }

    static async getFilterSandInfo(filter, allEquipment) {
        try {
            const response = await apiClient.post(`${API_BASE}/api/calculations/filters/sand`, { filter, allEquipment });
            const data = this.normalizeResponse(response.data).data;
            return data?.sandItems || [];
        } catch (e) {
            console.error(e); return [];
        }
    }

    // 4. Моноблоки
    static async getMonoblockRecommendations(allEquipment, poolData, requestOptions = {}) {
        const data = await this.getRecommendations('monoblock', { allEquipment, poolData }, requestOptions);
        if (data === undefined) return undefined;
        return data?.recommendations || [];
    }

    static async getMonoblockSandInfo(monoblock, allEquipment) {
        try {
            const response = await apiClient.post(`${API_BASE}/api/calculations/monoblock/sand`, { monoblock, allEquipment });
            const data = this.normalizeResponse(response.data).data;
            return data?.sandItems || [];
        } catch (e) {
            console.error(e); return [];
        }
    }

    // 5. Консоли
    static async getConsoleRecommendations(selectedEquipment) {
        try {
            const response = await apiClient.post(`${API_BASE}/api/calculations/consoles/recommend`, { selectedEquipment });
            const data = this.normalizeResponse(response.data).data;
            return data?.consoles || [];
        } catch (e) {
            console.error(e); return [];
        }
    }

    static async createDynamicConsole(state) {
        try {
            const safeState = this.sanitizeConsoleState(state);
            const response = await apiClient.post(`${API_BASE}/api/calculations/consoles/dynamic`, { state: safeState });
            const data = this.normalizeResponse(response.data).data;
            return data?.consoleObj || null;
        } catch (e) {
            console.error(e); return null;
        }
    }

    static async getConnectedEquipmentNames(state) {
        try {
            const safeState = this.sanitizeConsoleState(state);
            const response = await apiClient.post(`${API_BASE}/api/calculations/consoles/connected`, { state: safeState });
            const data = this.normalizeResponse(response.data).data;
            return data?.equipmentNames || [];
        } catch (e) {
            console.error(e); return [];
        }
    }

    // 6. Подогрев
    static async getHeatExchangerRecommendations(poolDimensions, allEquipment, selectedPump = null, requestOptions = {}) {
        const data = await this.getRecommendations('heat-exchangers', {
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : [],
            selectedPump
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [], calculations: {} };
    }

    static async getHeatExchangerSystem(selectedExchanger, quantity = 1) {
        try {
            const response = await apiClient.post(`${API_BASE}/api/calculations/heat-exchangers/system`, { selectedExchanger, quantity });
            return this.normalizeResponse(response.data).data || { heatExchanger: null, accessories: [] };
        } catch (e) {
            console.error(e); return { heatExchanger: null, accessories: [] };
        }
    }

    static async getElectricHeaterRecommendations(poolDimensions, allEquipment, selectedPump = null, requestOptions = {}) {
        const data = await this.getRecommendations('electric-heaters', {
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : [],
            selectedPump
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [], calculations: {} };
    }

    static async getHeatPumpRecommendations(poolDimensions, allEquipment, quantity = 1, requestOptions = {}) {
        const data = await this.getRecommendations('heatpump', {
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : [],
            quantity
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [], calculations: {} };
    }

    static async getHeaterTechnicalInfo(heater, poolData, deltaT = 15, pumpQty = 1) {
        const data = await this.getTechnicalInfo('heaters', { heater, poolData, deltaT, pumpQty });
        return data || null;
    }

    // 7. Ультрафиолет
    static async getUltravioletRecommendations(selectedPump, poolDimensions, allEquipment, requestOptions = {}) {
        const data = await this.getRecommendations('ultraviolet', {
            selectedPump: selectedPump || { quantity: 1 },
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : []
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [], calculations: {} };
    }

    static async getUltravioletTechnicalInfo(uvDevice, pumpFlowRate, pumpQty = 1) {
        const data = await this.getTechnicalInfo('ultraviolet', { uvDevice, pumpFlowRate, pumpQty });
        return data || null;
    }

    // 8. ПВХ-плёнка
    static async getPVCRecommendations(poolDimensions, allEquipment, requestOptions = {}) {
        const data = await this.getRecommendations('pvc-film', {
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : []
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [], autoMaterials: [], calculations: null, areasById: {} };
    }

    static async getMosaicRecommendations(poolDimensions, allEquipment) {
        const data = await this.getRecommendations('mosaic', {
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : []
        });
        return data || { recommendations: [], calculations: null };
    }

    static async getMosaicArea(poolDimensions, requestOptions = {}) {
        try {
            const response = await apiClient.post(
                `${API_BASE}/api/calculations/mosaic/area`,
                { poolDimensions: poolDimensions || {} },
                requestOptions
            );
            const data = this.normalizeResponse(response.data).data;
            const a = data?.area;
            return typeof a === 'number' && Number.isFinite(a) ? a : 0;
        } catch (e) {
            if (axios.isCancel?.(e) || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
                return undefined;
            }
            console.error(e);
            return 0;
        }
    }

    // 9. Лестницы
    static async getLadderRecommendations(poolDimensions, allEquipment, poolType = null, requestOptions = {}) {
        const data = await this.getRecommendations('ladders', {
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : [],
            poolType
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [] };
    }

    static async getLadderTechnicalInfo(poolDimensions, allEquipment, selectedDrain = null, poolType = null, requestOptions = {}) {
        const data = await this.getTechnicalInfo('ladders', {
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : [],
            selectedDrain,
            poolType
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [], quantity: 1, requiredSteps: 2, poolType: poolType || 'скиммерный', formulas: {} };
    }

    // 10. Донные сливы
    static async getDrainRecommendations(poolDimensions, allEquipment, selectedFinishing = null, selectedPump = null, requestOptions = {}) {
        const data = await this.getRecommendations('drains', {
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : [],
            selectedFinishing,
            selectedPump: selectedPump || null,
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [] };
    }

    static async getDrainTechnicalInfo(selectedDrain, poolDimensions, selectedPump = null) {
        const data = await this.getTechnicalInfo('drains', {
            selectedDrain,
            poolDimensions: poolDimensions || {},
            selectedPump: selectedPump || null,
        });
        return data || null;
    }

    // 11. Скиммеры
    static async getSkimmerRecommendations(poolDimensions, allEquipment, selectedFinishing = null, selectedPump = null, requestOptions = {}) {
        const data = await this.getRecommendations('skimmers', {
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : [],
            selectedFinishing,
            selectedPump: selectedPump || null,
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [] };
    }

    static async getSkimmerTechnicalInfo(selectedSkimmer, poolDimensions) {
        const data = await this.getTechnicalInfo('skimmers', {
            selectedSkimmer,
            poolDimensions: poolDimensions || {}
        });
        return data || null;
    }

    // 12. Форсунки
    static async getNozzleRecommendations(allEquipment, context = {}, requestOptions = {}) {
        const data = await this.getRecommendations('nozzles', {
            allEquipment: Array.isArray(allEquipment) ? allEquipment : [],
            context: context || {}
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [] };
    }

    static async getNozzleTechnicalInfo(selectedNozzle, selectedSkimmer) {
        const data = await this.getTechnicalInfo('nozzles', { selectedNozzle, selectedSkimmer });
        return data || null;
    }

    // 13. Переливные решетки (grate)
    static async getGrateRecommendations(poolDimensions, allEquipment, selectedPump = null, requestOptions = {}) {
        const data = await this.getRecommendations('grate', {
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : [],
            selectedPump: selectedPump || null
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [], perimeter: 0, requirements: null };
    }

    static async getGrateTechnicalInfo(selectedGrate, poolDimensions) {
        const data = await this.getTechnicalInfo('grate', {
            selectedGrate,
            poolDimensions: poolDimensions || {}
        });
        return data || null;
    }

    // 14. Освещение
    static async getLightingRecommendations(poolDimensions, allEquipment, requestOptions = {}) {
        const data = await this.getRecommendations('lighting', {
            poolDimensions: poolDimensions || {},
            allEquipment: Array.isArray(allEquipment) ? allEquipment : []
        }, requestOptions);
        if (data === undefined) return undefined;
        return data || { recommendations: [] };
    }

    static async getLightingTechnicalInfo(selectedProjector, poolDimensions, allEquipment, selectedPVC = null) {
        try {
            const response = await apiClient.post(`${API_BASE}/api/calculations/lighting/technical-info`, {
                selectedProjector,
                poolDimensions: poolDimensions || {},
                allEquipment: Array.isArray(allEquipment) ? allEquipment : [],
                selectedPVC
            });
            return this.normalizeResponse(response.data).data || { projector: null, transformer: [], embedded: null };
        } catch (e) {
            console.error(e);
            return { projector: null, transformer: [], embedded: null };
        }
    }

    static async getPVCArea(poolDimensions, options = {}) {
        const { signal, filmWidth, productName } = options;
        try {
            const response = await apiClient.post(
                `${API_BASE}/api/calculations/pvc-film/area`,
                {
                    poolDimensions: poolDimensions || {},
                    filmWidth,
                    productName
                },
                signal ? { signal } : {}
            );
            const data = this.normalizeResponse(response.data).data;
            const a = data?.area;
            return typeof a === 'number' && Number.isFinite(a) ? a : 0;
        } catch (e) {
            if (axios.isCancel?.(e) || e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') {
                return undefined;
            }
            console.error(e);
            return 0;
        }
    }

    static async getPVCAutoMaterials(poolDimensions, allEquipment) {
        try {
            const response = await apiClient.post(`${API_BASE}/api/calculations/pvc-film/auto-materials`, {
                poolDimensions: poolDimensions || {},
                allEquipment: Array.isArray(allEquipment) ? allEquipment : []
            });
            const data = this.normalizeResponse(response.data).data;
            return data?.materials || [];
        } catch (e) {
            console.error(e);
            return [];
        }
    }
}
